import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2, AlertTriangle, Copy, Check } from "lucide-react";
import {
  getBusinessProfile,
  saveBusinessProfile,
  type BusinessProfile,
  type OfficeEntry,
} from "@/lib/admin-business.functions";

export const Route = createFileRoute("/_authenticated/admin/foretagsuppgifter")({
  head: () => ({
    meta: [
      { title: "Företagsuppgifter — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BusinessProfilePage,
});

const SOCIAL_KEYS = ["linkedin", "instagram", "youtube", "facebook"] as const;

function Field({
  label, value, onChange, placeholder, hint, textarea, required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  textarea?: boolean;
  required?: boolean;
}) {
  const missing = required && !value.trim();
  return (
    <label className="block">
      <span className="text-xs font-medium text-white/60 flex items-center gap-2">
        {label}
        {missing && (
          <span className="inline-flex items-center gap-1 text-amber-400">
            <AlertTriangle className="h-3 w-3" /> saknas
          </span>
        )}
      </span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className={`mt-1 w-full rounded-lg bg-white/[0.04] border px-3 py-2 text-sm text-white outline-none focus:border-white/30 ${missing ? "border-amber-500/50" : "border-white/10"}`}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`mt-1 w-full rounded-lg bg-white/[0.04] border px-3 py-2 text-sm text-white outline-none focus:border-white/30 ${missing ? "border-amber-500/50" : "border-white/10"}`}
        />
      )}
      {hint && <span className="mt-1 block text-[11px] text-white/35">{hint}</span>}
    </label>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <h2 className="text-sm font-semibold">{title}</h2>
      {description && <p className="mt-1 text-xs text-white/45">{description}</p>}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function BusinessProfilePage() {
  const load = useServerFn(getBusinessProfile);
  const save = useServerFn(saveBusinessProfile);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["business-profile"], queryFn: () => load({}) });
  const [form, setForm] = useState<BusinessProfile | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (p: BusinessProfile) =>
      save({
        data: {
          brand: p.brand, legal_name: p.legal_name, org_number: p.org_number, vat_id: p.vat_id,
          founded_year: p.founded_year, website: p.website, email: p.email, photo_email: p.photo_email,
          phone: p.phone, street_address: p.street_address, postal_code: p.postal_code, city: p.city,
          region: p.region, country: p.country, opening_hours: p.opening_hours, price_range: p.price_range,
          short_description: p.short_description, long_description: p.long_description,
          categories: p.categories, service_area: p.service_area, socials: p.socials,
          offices: p.offices, notes: p.notes,
        },
      }),
    onSuccess: (row) => {
      setForm(row);
      qc.setQueryData(["business-profile"], row);
    },
  });

  if (isLoading || !form) {
    return (
      <div className="p-8 text-white/60 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Laddar företagsuppgifter…
      </div>
    );
  }
  if (error) return <div className="p-8 text-rose-400 text-sm">Kunde inte läsa uppgifterna.</div>;

  const set = <K extends keyof BusinessProfile>(k: K, v: BusinessProfile[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const setOffice = (i: number, patch: Partial<OfficeEntry>) =>
    setForm((f) => (f ? { ...f, offices: f.offices.map((o, idx) => (idx === i ? { ...o, ...patch } : o)) } : f));

  const napBlock = [
    form.legal_name || form.brand,
    [form.street_address, `${form.postal_code} ${form.city}`.trim(), form.country === "SE" ? "Sverige" : form.country]
      .filter(Boolean)
      .join(", "),
    form.phone,
    form.email,
    form.website,
  ]
    .filter(Boolean)
    .join("\n");

  const missing: string[] = [];
  if (!form.phone.trim()) missing.push("Telefonnummer");
  if (!form.street_address.trim()) missing.push("Gatuadress");
  if (!form.postal_code.trim()) missing.push("Postnummer");
  form.offices.forEach((o) => {
    if (!o.street.trim()) missing.push(`Adress ${o.city}`);
  });
  if (!Object.values(form.socials).some((v) => v?.trim())) missing.push("Sociala profiler");

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-5 text-white">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Företagsuppgifter (NAP)</h1>
          <p className="text-xs text-white/45">
            Underlag för citeringar, kataloger och Google Business Profile. Uppdaterad{" "}
            {new Date(form.updated_at).toLocaleString("sv-SE")}
            {form.updated_by ? ` av ${form.updated_by}` : ""}.
          </p>
        </div>
        <button
          onClick={() => mutation.mutate(form)}
          disabled={mutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-[#42FFC9] px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
        >
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Spara
        </button>
      </header>

      {mutation.isError && <p className="text-sm text-rose-400">Kunde inte spara. Försök igen.</p>}
      {mutation.isSuccess && !mutation.isPending && <p className="text-sm text-emerald-400">Sparat.</p>}

      {missing.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="text-sm font-medium text-amber-300 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Saknas för korrekta citeringar
          </p>
          <p className="mt-1 text-xs text-amber-200/70">{missing.join(" · ")}</p>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">NAP att skicka till katalogerna</h2>
          <button
            onClick={() => {
              navigator.clipboard.writeText(napBlock);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-xs text-white/70 hover:bg-white/5"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Kopiera
          </button>
        </div>
        <pre className="mt-3 whitespace-pre-wrap rounded-lg bg-black/40 p-3 text-xs text-white/75">{napBlock}</pre>
      </div>

      <Section title="Identitet" description="Juridiskt namn används i citeringar; varumärket i publik text.">
        <Field label="Varumärke" value={form.brand} onChange={(v) => set("brand", v)} required />
        <Field label="Juridiskt namn" value={form.legal_name} onChange={(v) => set("legal_name", v)} required />
        <Field label="Organisationsnummer" value={form.org_number} onChange={(v) => set("org_number", v)} required />
        <Field label="Momsregistreringsnummer" value={form.vat_id} onChange={(v) => set("vat_id", v)} />
        <Field
          label="Grundat år"
          value={form.founded_year ? String(form.founded_year) : ""}
          onChange={(v) => set("founded_year", v ? Number(v.replace(/\D/g, "")) : null)}
        />
        <Field label="Prisnivå" value={form.price_range} onChange={(v) => set("price_range", v)} hint="Google Business: $, $$, $$$" />
      </Section>

      <Section title="Kontakt" description="Samma uppgifter måste stå identiskt i varje katalog.">
        <Field label="Telefon" value={form.phone} onChange={(v) => set("phone", v)} required hint="Format: +46 8 123 45 67" />
        <Field label="E-post" value={form.email} onChange={(v) => set("email", v)} required />
        <Field label="Webbplats" value={form.website} onChange={(v) => set("website", v)} required />
        <Field label="Öppettider" value={form.opening_hours} onChange={(v) => set("opening_hours", v)} />
      </Section>

      <Section title="Huvudadress" description="Adressen som används i strukturerad data och som primär citering.">
        <Field label="Gatuadress" value={form.street_address} onChange={(v) => set("street_address", v)} required />
        <Field label="Postnummer" value={form.postal_code} onChange={(v) => set("postal_code", v)} required />
        <Field label="Ort" value={form.city} onChange={(v) => set("city", v)} required />
        <Field label="Region/län" value={form.region} onChange={(v) => set("region", v)} />
        <Field label="Land" value={form.country} onChange={(v) => set("country", v)} />
      </Section>

      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Kontor</h2>
            <p className="mt-1 text-xs text-white/45">Endast orter med fysisk adress får egen Google-profil.</p>
          </div>
          <button
            onClick={() => set("offices", [...form.offices, { city: "", street: "", postal_code: "", phone: "", primary: false }])}
            className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-xs text-white/70 hover:bg-white/5"
          >
            <Plus className="h-3.5 w-3.5" /> Lägg till
          </button>
        </div>
        <div className="mt-4 space-y-3">
          {form.offices.map((o, i) => (
            <div key={i} className="rounded-xl border border-white/10 p-3 grid gap-3 sm:grid-cols-5">
              <Field label="Ort" value={o.city} onChange={(v) => setOffice(i, { city: v })} />
              <Field label="Gatuadress" value={o.street} onChange={(v) => setOffice(i, { street: v })} required />
              <Field label="Postnr" value={o.postal_code} onChange={(v) => setOffice(i, { postal_code: v })} />
              <Field label="Telefon" value={o.phone} onChange={(v) => setOffice(i, { phone: v })} />
              <div className="flex items-end justify-between gap-2">
                <label className="flex items-center gap-2 text-xs text-white/60">
                  <input
                    type="checkbox"
                    checked={o.primary}
                    onChange={(e) =>
                      set("offices", form.offices.map((x, idx) => ({ ...x, primary: idx === i ? e.target.checked : false })))
                    }
                  />
                  Huvudkontor
                </label>
                <button
                  onClick={() => set("offices", form.offices.filter((_, idx) => idx !== i))}
                  className="rounded-md p-1.5 text-white/50 hover:bg-white/5 hover:text-rose-400"
                  aria-label="Ta bort kontor"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Section title="Beskrivningar och kategorier" description="Används ordagrant i katalogtexter.">
        <Field label="Kort beskrivning" value={form.short_description} onChange={(v) => set("short_description", v)} textarea />
        <Field label="Lång beskrivning" value={form.long_description} onChange={(v) => set("long_description", v)} textarea />
        <Field
          label="Kategorier"
          value={form.categories.join(", ")}
          onChange={(v) => set("categories", v.split(",").map((s) => s.trim()).filter(Boolean))}
          hint="Kommaseparerat"
          textarea
        />
        <Field label="Serviceområde" value={form.service_area} onChange={(v) => set("service_area", v)} textarea />
      </Section>

      <Section title="Sociala profiler" description="Länkarna används som verifieringskedja i strukturerad data.">
        {SOCIAL_KEYS.map((k) => (
          <Field
            key={k}
            label={k[0].toUpperCase() + k.slice(1)}
            value={form.socials[k] ?? ""}
            onChange={(v) => set("socials", { ...form.socials, [k]: v })}
            placeholder="https://…"
          />
        ))}
      </Section>

      <Section title="Interna anteckningar">
        <Field label="Anteckning" value={form.notes} onChange={(v) => set("notes", v)} textarea />
      </Section>
    </div>
  );
}
